import React, { useState, useCallback } from 'react';
import axios from 'axios';

const COMPARE_API = 'http://103.17.193.168:5002/compare';
const BACKEND     = 'https://www.shorthandonlineexam.in';

// ── Exact replica of ColoredText in FetchPassageById ──
const ColoredText = ({ coloredWords }) => (
  <>
    {(coloredWords || []).map((w, i) => (
      <span key={i} style={{
        color: w.color === 'red' ? '#b91c1c' : w.color === 'green' ? '#047857' : '#111',
        fontWeight: w.color !== 'black' ? 600 : 400
      }}>
        {w.word}{' '}
      </span>
    ))}
  </>
);

export default function DebugCompare() {
  const [inputId,  setInputId]  = useState('');
  const [status,   setStatus]   = useState('');
  const [error,    setError]    = useState('');
  const [info,     setInfo]     = useState(null);   // { studentId, subjectId, qset, departmentId, examType }
  const [passages, setPassages] = useState(null);   // raw /student-passages response
  const [ignoreList, setIgnoreList] = useState([]);
  const [result,   setResult]   = useState(null);   // /compare response + marks

  const run = useCallback(async () => {
    const sid = inputId.trim();
    if (!sid) return;
    setError(''); setStatus(''); setInfo(null); setPassages(null); setResult(null);

    try {
      // ── Step 1: get subjectId / qset / departmentId (same as expertAdmin.js) ──
      setStatus('Fetching student info…');
      const infoResp = await axios.post(`${BACKEND}/get-student-passages`, { studentId: sid }, { withCredentials: true });
      const { subjectId, qset, departmentId, examType } = infoResp.data;
      setInfo({ studentId: sid, subjectId, qset, departmentId, examType });

      // ── Step 2: fetch passages (same as FetchPassageById useEffect) ──
      setStatus('Fetching passages…');
      const passResp = await axios.get(
        `${BACKEND}/student-passages/${subjectId}/${qset}/${sid}/${departmentId}`,
        { withCredentials: true }
      );
      const psg = passResp.data;
      setPassages(psg);

      // ── Step 3: fetch ignore list (same as FetchPassageById useEffect) ──
      setStatus('Fetching ignore list…');
      const ignResp = await axios.post(`${BACKEND}/student-active-passage`, {
        subjectId, qset, activePassage: 'A', studentId: sid, departmentId
      }, { withCredentials: true });
      const iList = ignResp.data.ignoreList || [];
      setIgnoreList(iList);

      // ── Step 4: compare — EXACT same payload as FetchPassageById.js ──
      //   text1 = passages['passageA']     (labeled modelAnswer in original code)
      //   text2 = passages['ansPassageA']  (labeled userAnswer in original code)
      setStatus('Comparing…');
      const cmpResp = await axios.post(COMPARE_API, {
        text1:       psg['passageA'],
        text2:       psg['ansPassageA'],
        ignore_list: iList,
        ignore_case: true
      }, { headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' } });

      const { colored_words, added, missed, spelling, grammar } = cmpResp.data;
      const total   = (added||[]).length + (missed||[]).length + (spelling||[]).length + (grammar||[]).length;
      const examT   = examType || 'SKILL';
      const raw     = examT === 'SKILL' ? Math.max(80 - total / 2, 0) : Math.max(50 - total / 3, 0);
      const rounded = Math.ceil(raw * 2) / 2;

      setResult({ colored_words, added, missed, spelling, grammar, total, raw, rounded,
                  pass: rounded >= 32 ? 'Pass' : 'Fail' });
      setStatus('');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error');
      setStatus('');
    }
  }, [inputId]);

  const s = {
    wrap:    { fontFamily: 'Segoe UI, sans-serif', padding: '20px', background: '#f5f5f5', minHeight: '100vh' },
    card:    { background: '#fff', borderRadius: 8, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 16 },
    h2:      { fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#1a237e' },
    row:     { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
    input:   { border: '1px solid #ccc', borderRadius: 6, padding: '9px 14px', fontSize: 15, width: 260 },
    btn:     { background: '#1a237e', color: '#fff', border: 'none', borderRadius: 6, padding: '9px 22px', fontSize: 15, cursor: 'pointer' },
    pill:    { background: '#e8eaf6', borderRadius: 20, padding: '3px 14px', fontSize: 13, display: 'inline-block', margin: '2px 4px' },
    mark:    { fontSize: 32, fontWeight: 800, color: '#1a237e' },
    grid2:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    pre:     { fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
               maxHeight: 280, overflowY: 'auto', background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 10 },
    colored: { fontSize: 14, lineHeight: 2.2, padding: 10, background: '#fafafa', border: '1px solid #eee', borderRadius: 6, maxHeight: 400, overflowY: 'auto' },
    err:     { background: '#ffebee', color: '#c62828', borderRadius: 6, padding: '10px 14px', fontSize: 14 },
    loader:  { color: '#555', fontSize: 14, padding: '8px 0' },
    passClr: (v) => ({ color: v === 'Pass' ? '#2e7d32' : '#c62828', fontWeight: 700, fontSize: 28 }),
  };

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.h2}>🔍 Debug Compare — Exact React Flow</div>
        <div style={s.row}>
          <input
            style={s.input}
            placeholder="Student ID (e.g. 211251610077)"
            value={inputId}
            onChange={e => setInputId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && run()}
          />
          <button style={s.btn} onClick={run}>Load &amp; Compare</button>
        </div>
        {status && <div style={s.loader}>⏳ {status}</div>}
        {error  && <div style={{ ...s.err, marginTop: 8 }}>⚠️ {error}</div>}
      </div>

      {info && (
        <div style={s.card}>
          <div style={s.row}>
            <span style={s.pill}>Student: <b>{info.studentId}</b></span>
            <span style={s.pill}>Subject: <b>{info.subjectId}</b></span>
            <span style={s.pill}>QSet: <b>{info.qset}</b></span>
            <span style={s.pill}>Dept: <b>{info.departmentId}</b></span>
            <span style={s.pill}>Exam: <b>{info.examType}</b></span>
          </div>
        </div>
      )}

      {result && (
        <>
          {/* Marks summary */}
          <div style={s.card}>
            <div style={s.row}>
              <div style={{ textAlign: 'center', minWidth: 120 }}>
                <div style={s.mark}>{result.raw.toFixed(2)}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Raw Marks / {info?.examType === 'SKILL' ? 80 : 50}</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: 120 }}>
                <div style={s.mark}>{result.rounded.toFixed(1)}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Rounded</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: 120 }}>
                <div style={s.mark}>{result.total}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Total Mistakes</div>
              </div>
              <div style={{ textAlign: 'center', minWidth: 120 }}>
                <div style={s.passClr(result.pass)}>{result.pass}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Result (≥32)</div>
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <span style={s.pill}>Spelling: <b>{(result.spelling||[]).length}</b></span>
              <span style={s.pill}>Added (missed): <b>{(result.missed||[]).length}</b></span>
              <span style={s.pill}>Omitted (added): <b>{(result.added||[]).length}</b></span>
              <span style={s.pill}>Grammar: <b>{(result.grammar||[]).length}</b></span>
            </div>
          </div>

          {/* Passages side by side */}
          {passages && (
            <div style={s.grid2}>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#666', marginBottom: 8 }}>
                  MODEL ANSWER (passages.ansPassageA → text2)
                </div>
                <pre style={s.pre}>{passages.ansPassageA || '(empty)'}</pre>
              </div>
              <div style={s.card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#666', marginBottom: 8 }}>
                  STUDENT ANSWER (passages.passageA → text1)
                </div>
                <pre style={s.pre}>{passages.passageA || '(empty)'}</pre>
              </div>
            </div>
          )}

          {/* Colored output */}
          <div style={s.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#666', marginBottom: 8 }}>DIFFERENCE PASSAGE</div>
            <div style={s.colored}>
              <ColoredText coloredWords={result.colored_words} />
            </div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 8 }}>
              <span style={{ color: '#b91c1c', fontWeight: 600 }}>■ Red</span> = in text1 (student) not matched &nbsp;
              <span style={{ color: '#047857', fontWeight: 600 }}>■ Green</span> = in text2 (model) not matched
            </div>
          </div>

          {/* Mistake details */}
          <div style={s.grid2}>
            <div style={s.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', marginBottom: 8 }}>
                EXTRA ADDED WORDS / missed ({(result.missed||[]).length})
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>{(result.missed||[]).join(', ') || 'None'}</div>
            </div>
            <div style={s.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#047857', marginBottom: 8 }}>
                OMITTED WORDS / added ({(result.added||[]).length})
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>{(result.added||[]).join(', ') || 'None'}</div>
            </div>
            <div style={s.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 8 }}>
                SPELLING ({(result.spelling||[]).length})
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                {(result.spelling||[]).map((p, i) =>
                  <span key={i}>
                    <span style={{ color: '#b91c1c' }}>{Array.isArray(p) ? p[0] : p}</span>
                    {Array.isArray(p) && <> → <span style={{ color: '#047857' }}>{p[1]}</span></>}
                    {i < result.spelling.length - 1 ? ', ' : ''}
                  </span>
                )}
              </div>
            </div>
            <div style={s.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#555', marginBottom: 8 }}>
                IGNORE LIST ({ignoreList.filter(w => !['////', '///', '//', '/'].includes(w)).length} words)
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                {ignoreList.filter(w => !['////', '///', '//', '/'].includes(w)).join(', ') || 'None'}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
